import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

export interface UserOption {
  employeeID: number;
  full_name: string;
  position?: string | null;
}

/**
 * Chips for the selected users plus a search box to add more.
 * A form control whose value is the selected user IDs; use it with ngModel or a FormControl.
 *
 * <app-user-picker [users]="users" [(ngModel)]="userIds"></app-user-picker>
 */
@Component({
  selector: 'app-user-picker',
  standalone: true,
  imports: [CommonModule, MatAutocompleteModule, MatChipsModule, MatFormFieldModule, MatIconModule],
  templateUrl: './user-picker.component.html',
  styleUrls: ['./user-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserPickerComponent),
      multi: true,
    },
  ],
})
export class UserPickerComponent implements ControlValueAccessor, OnChanges {
  /** Users that can be picked; also used to name the chips */
  @Input() users: UserOption[] = [];
  @Input() placeholder = 'Add user...';
  /** Shown in the search box while the control is disabled */
  @Input() disabledPlaceholder = '';
  @Input() ariaLabel = 'Users';
  /** Autocomplete shows at most this many matches, to keep the panel short */
  @Input() maxOptions = 50;

  /** Selected users as chips, sorted by name */
  chips: UserOption[] = [];
  /** Autocomplete matches for the current search */
  options: UserOption[] = [];
  disabled = false;

  private selectedIds: number[] = [];
  private usersById = new Map<number, UserOption>();
  private onChange: (ids: number[]) => void = () => {};
  private onTouched: () => void = () => {};

  /** Rebuilds the ID lookup when the user list changes, so chips pick up the new names */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users) {
      this.usersById = new Map((this.users || []).map((user) => [user.employeeID, user]));
      this.refreshChips();
    }
  }

  /** Called by the form to set the selected IDs; duplicates are dropped and null clears the selection */
  writeValue(ids: number[] | null): void {
    this.selectedIds = Array.isArray(ids) ? [...new Set(ids)] : [];
    this.refreshChips();
  }

  /** Called by the form to give us the callback we use to report new selections */
  registerOnChange(fn: (ids: number[]) => void): void {
    this.onChange = fn;
  }

  /** Called by the form to give us the callback we use to mark the control as touched */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Called by the form when the control is enabled or disabled */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Fills the autocomplete with unselected users whose name or position contains the search text */
  filterOptions(text: string): void {
    const search = (text || '').trim().toLowerCase();
    this.options = (this.users || [])
      .filter((user) => !this.selectedIds.includes(user.employeeID))
      .filter((user) => [user.full_name, user.position]
        .some((value) => (value || '').toLowerCase().includes(search)))
      .slice(0, this.maxOptions);
  }

  /** Adds the user picked from the autocomplete and clears the search box */
  add(event: MatAutocompleteSelectedEvent, input: HTMLInputElement): void {
    const user: UserOption = event.option.value;
    input.value = '';
    if (this.disabled || this.selectedIds.includes(user.employeeID)) return;
    this.update([...this.selectedIds, user.employeeID]);
  }

  /** Removes the user whose chip was removed */
  remove(user: UserOption): void {
    if (this.disabled) return;
    this.update(this.selectedIds.filter((id) => id !== user.employeeID));
  }

  /** Marks the control as touched when the search box loses focus */
  onBlur(): void {
    this.onTouched();
  }

  /** Tracks chips and options by employee ID */
  trackByUser(_index: number, user: UserOption): number {
    return user.employeeID;
  }

  /** Stores the new selection, refreshes chips and options, and reports the change to the form */
  private update(ids: number[]): void {
    this.selectedIds = ids;
    this.refreshChips();
    this.filterOptions('');
    this.onChange([...ids]);
  }

  /** Rebuilds the chips from the selected IDs, sorted by name */
  private refreshChips(): void {
    // An ID with no matching user (e.g. no longer active) still shows, so it can be removed deliberately
    this.chips = this.selectedIds
      .map((id) => this.usersById.get(id) || { employeeID: id, full_name: `User #${id}` })
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }
}
